export const COMPANY_PAYMENT_BLOCKED = 'COMPANY_PAYMENT_BLOCKED';
export const COMPANY_SELECTION_REQUIRED = 'COMPANY_SELECTION_REQUIRED';
export const COMPANY_PAYMENT_WARNING_MESSAGE = 'Tiene facturas vencidas. Regularice su pago a la brevedad para evitar el corte. Comuníquese con el departamento de finanzas.';
export const COMPANY_PAYMENT_MESSAGE = 'El acceso al programa está suspendido por falta de pago. Comuníquese con el departamento de finanzas.';
export const BLOCKED_COMPANY_STORAGE_KEY = 'blockedCompany';
export const PAYMENT_WARNING_SESSION_STORAGE_KEY = 'companyPaymentWarningSession';
let paymentBlockedHandler = () => {};

export const setCompanyPaymentHandler = handler => { paymentBlockedHandler = handler; };

export const readBlockedCompany = () => {
  try {
    return JSON.parse(localStorage.getItem(BLOCKED_COMPANY_STORAGE_KEY));
  } catch (_) {
    return null;
  }
};

export const isCompanyPaymentBlockedError = error => Boolean(
  error?.paymentBlocked ||
  (error?.response?.status === 403 && error.response.data?.errorCode === COMPANY_PAYMENT_BLOCKED)
);

export const isCompanyAccessError = error => isCompanyPaymentBlockedError(error) || Boolean(
  error?.companySelectionRequired ||
  (error?.response?.status === 403 && error.response.data?.errorCode === COMPANY_SELECTION_REQUIRED)
);

export const isCompanyRecoveryPath = pathname => [
  '/company-suspended', '/companyGroup', '/companySelector'
].includes(pathname);

const tokenClaims = token => {
  try {
    return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch (_) {
    return null;
  }
};

export const readPaymentWarningSession = (token = localStorage.getItem('token')) => {
  try {
    const session = JSON.parse(sessionStorage.getItem(PAYMENT_WARNING_SESSION_STORAGE_KEY));
    return session && tokenClaims(token)?.jti === session.jti ? session : null;
  } catch (_) {
    return null;
  }
};

export const initializePaymentWarningSession = (token, companyIds) => {
  const jti = tokenClaims(token)?.jti;
  if (!jti) {
    sessionStorage.removeItem(PAYMENT_WARNING_SESSION_STORAGE_KEY);
    return;
  }
  sessionStorage.setItem(PAYMENT_WARNING_SESSION_STORAGE_KEY, JSON.stringify({
    jti,
    warningCompanyIds: Array.isArray(companyIds) ? companyIds.map(id => id.toLowerCase()) : [],
    acknowledgedCompanyIds: [],
    pendingWarningCompany: null
  }));
};

export const clearPendingPaymentWarning = () => {
  const session = readPaymentWarningSession();
  if (session) {
    session.pendingWarningCompany = null;
    sessionStorage.setItem(PAYMENT_WARNING_SESSION_STORAGE_KEY, JSON.stringify(session));
  }
};

export const selectedCompanyPaymentWarning = (companyId, isPaymentWarning, token) => {
  const session = readPaymentWarningSession(token);
  if (!session) return null;
  const id = companyId.toLowerCase();
  const eligible = isPaymentWarning === true && session.warningCompanyIds.includes(id) &&
    !session.acknowledgedCompanyIds.includes(id);
  session.pendingWarningCompany = eligible ? { companyId: id, sessionId: session.jti } : null;
  sessionStorage.setItem(PAYMENT_WARNING_SESSION_STORAGE_KEY, JSON.stringify(session));
  return session.pendingWarningCompany;
};

export const acknowledgePaymentWarning = warning => {
  const session = readPaymentWarningSession();
  if (!session) return null;
  if (session.jti === warning.sessionId && session.pendingWarningCompany?.companyId === warning.companyId) {
    if (!session.acknowledgedCompanyIds.includes(warning.companyId))
      session.acknowledgedCompanyIds.push(warning.companyId);
    session.pendingWarningCompany = null;
    sessionStorage.setItem(PAYMENT_WARNING_SESSION_STORAGE_KEY, JSON.stringify(session));
  }
  return session.pendingWarningCompany;
};

export const isSameCompanySession = (requestToken, currentToken) => {
  if (!currentToken) return false;
  if (requestToken === currentToken) return true;
  const request = tokenClaims(requestToken);
  const current = tokenClaims(currentToken);
  return Boolean(request && current && request.User_ID === current.User_ID &&
    request.Company_ID === current.Company_ID && request.jti === current.jti);
};

const readBlob = blob => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsText(blob);
});

const notifyPaymentBlocked = (error, data, onBlocked) => {
  if (data?.errorCode === COMPANY_SELECTION_REQUIRED) {
    error.companySelectionRequired = true;
    onBlocked({ errorCode: COMPANY_SELECTION_REQUIRED }, error);
    return true;
  }
  if (data?.errorCode !== COMPANY_PAYMENT_BLOCKED) return false;
  error.paymentBlocked = true;
  onBlocked({ companyId: data.companyId, message: COMPANY_PAYMENT_MESSAGE }, error);
  return true;
};

export const apiFetch = async (...args) => {
  const response = await fetch(...args);
  if (response.status === 403) {
    const data = await response.clone().json().catch(() => null);
    const error = new Error(data?.message || COMPANY_PAYMENT_MESSAGE);
    error.response = { status: response.status, data };
    error.config = { headers: args[1]?.headers };
    if (notifyPaymentBlocked(error, data, paymentBlockedHandler)) throw error;
  }
  return response;
};

export const installCompanyPaymentInterceptor = (client, onBlocked) => {
  const interceptor = client.interceptors.response.use(response => response, async error => {
    if (error?.response?.status === 403) {
      let data = error.response.data;
      if (typeof Blob !== 'undefined' && data instanceof Blob) {
        try {
          data = JSON.parse(await readBlob(data));
        } catch (_) {
          data = null;
        }
      }
      notifyPaymentBlocked(error, data, onBlocked);
    }
    return Promise.reject(error);
  });
  return () => client.interceptors.response.eject(interceptor);
};
